import MainLayout from 'Components/Layout/MainLayout';
import {Button, Dropdown, message, Space, Spin} from 'antd';
import {UserAddOutlined} from '@ant-design/icons';
import React, {useRef} from 'react';
import Style from './index.module.scss';
import TableRender, {ProColumnsType, TableContext} from 'table-render';
import useTable from '../../../../Utils/Hooks/useTable';
import {
    changeTenantStatus,
    createCustomerLicense,
    deleteTenant,
    GetTenant,
    getTenantPage,
    TenantType,
} from '../service';
import useModalHandler from '../../../../Utils/Hooks/useModalHandler';
import ProTable from '../../../../Components/ProTable';
import {Link, useNavigate} from 'react-router-dom';
import DatabaseModal from './Components/DatabaseModal';
import PasswordModal from './Components/PasswordModal';
import {useDict} from '../dict';
import SelectDict from '../../../../Features/Select/SelectDict';
import {Status} from '../../../../Services/common';
import useRequestFn from '../../../../Utils/Hooks/useRequestFn';
import LoginInfoModal from './Components/LoginInfoModal';
import {errorMessage, filterHiddenListItem} from '../../../../Utils/common';
import DeleteConfirm from '../../../../Components/DeleteConfirm';
import DefaultServiceModal from '../TenantDetail/Components/OrderHistory/Components/DefaultServiceModal';
import {LinkButton} from '../../../../Features/Buttons/LinkButton';
import PrivateModal from './Components/PrivateModal';
import EditUuidModal from './Components/EditUuidModal';

export default function Index() {
    const dict = useDict();
    const navigate = useNavigate();
    const {tableInit} = useTable(getTenantPage, {pageSize: 999});
    const tableRef = useRef<TableContext>(null);
    const databaseModal = useModalHandler({
        component: DatabaseModal,
    });
    const loginInfoModal = useModalHandler({
        component: LoginInfoModal,
    });
    const passwordModal = useModalHandler({
        component: PasswordModal,
    });
    const defaultServiceModal = useModalHandler({
        component: DefaultServiceModal,
    });
    const privateModal = useModalHandler({
        component: PrivateModal,
    });
    const editUuidModal = useModalHandler({
        component: EditUuidModal,
    });

    function reload(stay = true) {
        tableRef?.current?.refresh({
            stay,
        });
    }

    const toolBar = (
        <div className={Style.toolBar}>
            <Space>
                <Dropdown
                    menu={{
                        items: [
                            {
                                label: 'SaaS云服务',
                                key: '1',
                                onClick: () => {
                                    navigate(`info`);
                                },
                            },
                            {
                                label: '私有化服务',
                                key: '2',
                                onClick: () => {
                                    privateModal.open({
                                        onOk: () => {
                                            reload();
                                            privateModal.close();
                                        },
                                    });
                                },
                            },
                        ],
                    }}>
                    <Button type="primary" icon={<UserAddOutlined />}>
                        添加客户
                    </Button>
                </Dropdown>
            </Space>
        </div>
    );

    const schema = {
        type: 'object',
        labelWidth: 100,
        properties: {
            name: {
                title: '客户名称',
                type: 'string',
            },
            status: {
                title: '客户状态',
                type: 'string',
                widget: 'SelectDict',
                props: {
                    options: dict.TenantStatus.data,
                },
            },
            expirationEndTime: {
                tooltip: '到期时间非空代表查询所选到期时间后一个月内的记录。',
                title: '到期时间',
                type: 'string',
                widget: 'datePicker',
                props: {},
            },
        },
    };

    function onPasswodOk() {
        passwordModal.close();
        reload();
    }

    const [changeStatusAction, {loading: changeLoading}] =
        useRequestFn(changeTenantStatus);

    async function changeStatus(id: string, status: Status) {
        try {
            await changeStatusAction(id, status);
            await reload();
        } catch (e) {
            message.error(errorMessage(e));
        }
    }

    const [deleteAction, {loading: deleteLoading}] = useRequestFn(deleteTenant);

    async function onDelete(id: string) {
        try {
            await deleteAction(id);
            await reload();
        } catch (e) {
            message.error(errorMessage(e));
        }
    }

    const columns: ProColumnsType<GetTenant> = [
        {
            title: '客户名称',
            dataIndex: 'name',
            width: 150,
            render: (name, item) => (
                <Link to={`detail/${item.id}`}>{name}</Link>
            ),
        },
        {title: '客户编码', dataIndex: 'code', width: 120},
        {title: '开通方案', dataIndex: 'versionName', width: 150},
        {
            title: '客户状态',
            dataIndex: 'status',
            width: 120,
            render: dict.TenantStatus.getName,
        },
        {
            title: '客户服务类型',
            dataIndex: 'type',
            width: 120,
            render: dict.TenantType.getName,
        },
        {title: '到期时间', dataIndex: 'expirationEndTime', width: 160},
        {title: '创建时间', dataIndex: 'createTime', width: 160},
        {title: '客户账号', dataIndex: 'adminLoginName', width: 120},
        {
            title: '操作',
            fixed: 'right',
            dataIndex: 'id',
            align: 'center',
            render: (id: string, item) => (
                <ProTable.Operate>
                    <Button
                        size="small"
                        type="link"
                        onClick={() => {
                            if (item.type === TenantType.SaaS) {
                                navigate(`info/${id}`);
                            }
                            if (item.type === TenantType.Private) {
                                privateModal.open({
                                    id,
                                    onOk: () => {
                                        reload();
                                        privateModal.close();
                                    },
                                });
                            }
                        }}>
                        编辑
                    </Button>
                    {item.type === TenantType.Private && (
                        <Button
                            size="small"
                            type="link"
                            onClick={() => {
                                editUuidModal.open({
                                    customerId: id,
                                    onOk: () => {
                                        reload();
                                        editUuidModal.close();
                                    },
                                });
                            }}>
                            维护UUID
                        </Button>
                    )}
                    <DeleteConfirm onSuccess={() => onDelete(id)}>
                        <Button size="small" type="link">
                            删除
                        </Button>
                    </DeleteConfirm>
                    <Dropdown
                        menu={{
                            items: filterHiddenListItem([
                                {
                                    label: '登录信息',
                                    key: '1',
                                    onClick: () => {
                                        loginInfoModal.open({
                                            id,
                                        });
                                    },
                                },
                                {
                                    label: '修改密码',
                                    key: '2',
                                    onClick: () => {
                                        passwordModal.open({
                                            id,
                                            onOk: onPasswodOk,
                                        });
                                    },
                                },
                                {
                                    label: '数据库',
                                    key: '3',
                                    onClick: () => {
                                        databaseModal.open({
                                            id,
                                        });
                                    },
                                },
                                {
                                    label: '购买服务',
                                    key: '4',
                                    onClick: () => {
                                        defaultServiceModal.open({
                                            customerId: id,
                                            onOk: () => {
                                                reload();
                                                defaultServiceModal.close();
                                            },
                                        });
                                    },
                                },
                                {
                                    label: '已开通服务',
                                    key: '5',
                                    onClick: () => {
                                        navigate(`service/${id}`);
                                    },
                                },
                                {
                                    label:
                                        item.status2 === Status.Freeze
                                            ? '启用'
                                            : '停用',
                                    key: '6',
                                    onClick: () => {
                                        changeStatus(
                                            id,
                                            item.status2 === Status.Freeze
                                                ? Status.Normal
                                                : Status.Freeze,
                                        );
                                    },
                                },
                                {
                                    label: '打包下载配置相关信息',
                                    key: '7',
                                    hidden: item.type !== TenantType.Private,
                                    onClick: () => {
                                        createCustomerLicense(id).catch(e =>
                                            message.error(errorMessage(e)),
                                        );
                                    },
                                },
                            ]),
                        }}>
                        <LinkButton>更多</LinkButton>
                    </Dropdown>
                </ProTable.Operate>
            ),
        },
    ];

    return (
        <MainLayout.SpaceWrapper padding>
            <Spin spinning={changeLoading || deleteLoading}>
                <TableRender
                    pageChangeWithRequest={false}
                    rowKey="id"
                    ref={tableRef}
                    search={{schema, widgets: {SelectDict}}}
                    columns={columns}
                    toolbarRender={toolBar}
                    scroll={{x: 'max-content'}}
                    {...tableInit()}
                />
            </Spin>

            {databaseModal.render()}
            {passwordModal.render()}
            {loginInfoModal.render()}
            {defaultServiceModal.render()}
            {privateModal.render()}
            {editUuidModal.render()}
        </MainLayout.SpaceWrapper>
    );
}
